/** @jsx jsx */
import React, { useCallback, useEffect, useMemo, useRef, type KeyboardEvent } from 'react';
import { jsx } from '@compiled/react';
import type { ButtonProps } from '@atlaskit/button/new';
import CrossIcon from '@atlaskit/icon/core/cross';
import SelectAllIcon from '@atlaskit/icon/core/presenter-mode';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { cssMap } from '@atlaskit/css';
import type { AnyEntryPoint } from '@atlassian/jira-entry-point/src/common/types.tsx';
import { FormattedMessage, useIntl } from '@atlassian/jira-intl';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { expVal } from '@atlassian/jira-feature-experiments';
import { BULK_OPS_SELECTED_ISSUE_LIMIT } from '@atlassian/jira-bulk-operations-common/src/common/constants.tsx';
import type { ToolbarAction } from '../common/types.tsx';
import {
	FloatingToolbarContainer,
	FloatingToolbarContent,
	FloatingToolbarSeparator,
	FloatingToolbarSideContent,
} from '../common/ui/index.tsx';
import messages from './messages.tsx';
import { ToolbarButton } from './toolbar-button/index.tsx';

const styles = cssMap({
	selectedContainer: {
		font: token('font.body'),
		color: token('color.text.inverse'),
		height: '16px',
		display: 'flex',
		flexWrap: 'wrap',
		alignItems: 'center',
		alignContent: 'center',
	},
	selectedContainerNew: {
		color: token('color.text'),
	},
	numberContainer: {
		color: token('color.text.subtle'),
		backgroundColor: token('color.background.accent.gray.subtlest.hovered'),
		height: '16px',
		minWidth: '24px',
		marginRight: token('space.050'),
		borderRadius: token('radius.xsmall'),
		display: 'flex',
		justifyContent: 'center',
		alignContent: 'center',
		flexWrap: 'wrap',
		alignItems: 'center',
	},
});

export type FloatingToolbarProps<TEntryPoint extends AnyEntryPoint | undefined = undefined> = {
	numberOfSelectedItems: number;
	isAllSelected: boolean;
	onClearAll: ButtonProps['onClick'];
	onSelectAll?: ButtonProps['onClick'];
	actions?: ToolbarAction<TEntryPoint>[];
	shouldDisableButtons?: boolean;
	showSelectFirstThreshold?: boolean;
};

export const FloatingToolbar = <TEntryPoint extends AnyEntryPoint | undefined = undefined>({
	numberOfSelectedItems,
	actions,
	shouldDisableButtons,
	onClearAll,
	onSelectAll,
	showSelectFirstThreshold,
}: FloatingToolbarProps<TEntryPoint>) => {
	const { formatMessage } = useIntl();

	const buttonsRefs = useRef(new Map<number, HTMLButtonElement | null>());
	const clearAllButtonRef = useRef<HTMLButtonElement | null>(null);
	const selectAllButtonRef = useRef<HTMLButtonElement | null>(null);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		fireUIAnalytics(createAnalyticsEvent({}), 'bulkToolbar viewed', 'bulkFloatingToolbar', {});
	}, [createAnalyticsEvent]);

	const buttons = useMemo(() => {
		const buttonsComponents = actions?.map((action, index) => {
			return (
				<ToolbarButton
					tabIndex={!onSelectAll && index === 0 ? 0 : -1}
					key={index}
					action={action}
					isDisabled={shouldDisableButtons}
					buttonRef={(elem: HTMLButtonElement) => {
						const mapElem = elem || null;
						buttonsRefs.current.set(index, mapElem);
					}}
				/>
			);
		});

		return buttonsComponents;
	}, [onSelectAll, actions, shouldDisableButtons]);

	const onKeyDownHandler = useCallback(
		(e: KeyboardEvent<HTMLElement>) => {
			if (['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp', 'Home', 'End'].includes(e.key)) {
				e.stopPropagation();

				if (e.key === 'End') {
					clearAllButtonRef.current?.focus();
					return;
				}
				if (e.key === 'Home') {
					if (onSelectAll) {
						selectAllButtonRef.current?.focus();
					} else {
						buttonsRefs.current.get(0)?.focus();
					}
					return;
				}
				if (typeof document !== 'undefined') {
					const middleButtons = Array.from(buttonsRefs.current.values()).filter(
						(item): item is HTMLButtonElement => Boolean(item),
					);

					const allButtons = [
						...(selectAllButtonRef.current ? [selectAllButtonRef.current] : []),
						...middleButtons,
						...(clearAllButtonRef.current ? [clearAllButtonRef.current] : []),
					];
					const focusedElement = document.activeElement;
					const moveLeft = e.key === 'ArrowLeft' || e.key === 'ArrowUp';
					const moveRight = e.key === 'ArrowDown' || e.key === 'ArrowRight';
					const focusedIndex = allButtons.findIndex((b) => b === focusedElement);

					let newFocus: number | undefined;
					if (moveRight) {
						if (focusedIndex === allButtons.length - 1) {
							newFocus = 0;
						} else {
							newFocus = focusedIndex + 1;
						}
					}
					if (moveLeft) {
						if (focusedIndex === 0) {
							newFocus = allButtons.length - 1;
						} else {
							newFocus = focusedIndex - 1;
						}
					}
					if (newFocus !== undefined) {
						allButtons[newFocus].focus();
					}
				}
			}
		},
		[onSelectAll],
	);

	return (
		<FloatingToolbarContainer onKeyDown={onKeyDownHandler}>
			<FloatingToolbarContent>
				<FloatingToolbarSideContent>
					{/* eslint-disable-next-line @atlaskit/design-system/use-heading */}
					<h2
						aria-live="polite"
						css={[
							styles.selectedContainer,
							expVal('sunsetting_bulk_edit', 'isEnabled', false) && styles.selectedContainerNew,
						]}
					>
						<FormattedMessage
							{...messages.selectedItemsLabel}
							values={{
								numberOfSelectedItems,
								numberSelected: (
									<Box
										paddingInlineStart="space.050"
										paddingInlineEnd="space.050"
										xcss={styles.numberContainer}
									>
										{numberOfSelectedItems}
									</Box>
								),
							}}
						/>
					</h2>
				</FloatingToolbarSideContent>
				{onSelectAll && (
					<ToolbarButton
						tabIndex={0}
						isDisabled={shouldDisableButtons}
						buttonRef={selectAllButtonRef}
						action={{
							icon: SelectAllIcon,
							name:
								showSelectFirstThreshold &&
								expVal('bulk_ops_select_all_beyond_experiment', 'isEnabled', false)
									? formatMessage(messages.selectFirstButtonLabel, {
											numberOfIssues: BULK_OPS_SELECTED_ISSUE_LIMIT,
										})
									: formatMessage(messages.selectAllButtonLabel),
							type: 'perform',
							onClick: onSelectAll,
						}}
					/>
				)}
				<FloatingToolbarSeparator />
				{buttons}
				<ToolbarButton
					tabIndex={-1}
					isDisabled={shouldDisableButtons}
					buttonRef={clearAllButtonRef}
					action={{
						icon: CrossIcon,
						name: formatMessage(messages.clearAllButton),
						type: 'perform',
						onClick: onClearAll,
						iconOnly: true,
					}}
				/>
			</FloatingToolbarContent>
		</FloatingToolbarContainer>
	);
};
